import './blocks.less'

import {DOMAttrs, m} from '@codeurs/front'
import {Component, FunctionComponent} from 'react'
import {style} from 'util/style'
import {TextBlock, TextBlockType} from 'views/blocks/textblock'
import {AnchorBlock} from './anchorblock'
import {AudioBlock, AudioBlockType} from './audioblock'
import {ButtonBlock, ButtonBlockType} from './buttonblock'
import {ChartBlock, ChartBlockType} from './chartblock'
import {CollectionBlock, CollectionBlockType} from './collectionblock'
import {ColumnBlock, ColumnBlockType} from './columnblock'
import {
	CookiesdeclarationBlock,
	CookiesdeclarationBlockType
} from './cookiedeclarationblock'
import {DividerBlock, DividerBlockType} from './dividerblock'
import {EmbedBlock, EmbedBlockType} from './embedblock'
import {HarmonicaBlock, HarmonicaBlockType} from './harmonicablock'
import {ImageBlock, ImageBlockType} from './imageblock'
import {ImageTextBlock, ImageTextBlockType} from './imagetextblock'
import {IntroBlock, IntroBlockType} from './introblock'
import {MatomoBlock} from './matomoblock'
import {PersonBlock} from './personblock'
import {PreviewLinkBlock, PreviewLinkType} from './previewlinkblock'
import {PreviewLinkGroupBlock} from './previewlinkgroupblock'
import {QuoteBlock, QuoteBlockType} from './quoteblock'
import {ReferencesBlock, ReferencesBlockType} from './referencesblock'

const styles = style('blocks', ['block'])

export type BlockType =
	| {type: 'audio'} & AudioBlockType
	| {type: 'intro'} & IntroBlockType
	| {type: 'text'} & TextBlockType
	| {type: 'image'} & ImageBlockType
	| {type: 'imagetext'} & ImageTextBlockType
	| {type: 'references'} & ReferencesBlockType
	| {type: 'button'} & ButtonBlockType
	| {type: 'harmonica'} & HarmonicaBlockType
	| {type: 'collection'} & CollectionBlockType
	| {type: '2columns'} & ColumnBlockType
	| {type: 'previewlink'} & PreviewLinkType
	| {type: 'previewlinkgroup'} & {links: Array<PreviewLinkType>}
	| {type: 'embed'} & EmbedBlockType
	| {type: 'quote'} & QuoteBlockType
	| {type: 'chart'} & ChartBlockType
	| {type: 'divider'} & DividerBlockType
	| {type: 'cookiedeclaration'} & CookiesdeclarationBlockType
	| {type: 'matomo'}

export class Blocks extends Component<{
	blocks: Array<BlockType>
	className?: string
	pageId: number
	wide?: boolean
}> {
	skipindex = 0
	render() {
		const {blocks, className, pageId, wide} = this.props

		if (!blocks || !blocks.length) return null
		if (blocks.length === 1) {
			const firstBlock = blocks[0]
			if (firstBlock.type === 'text') {
				if (!firstBlock.text) return null
			}
		}

		return (
			<div class={styles.with(className).mod({wide})}>
				{blocks.map((block, i) => {
					if (this.skipindex > 0) {
						this.skipindex--
						return
					}
					const blockKey = `key${pageId}nr${i}`
					if (block.type === 'harmonica') {
						return this.catchHarmonicaBlocks(blocks, block, i, blockKey)
					}

					return renderSwitch(block, blockKey)
				})}
			</div>
		)
	}

	catchHarmonicaBlocks(allblocks, harmonicaBlock, i, blockKey) {
		const content = []
		// take next block
		const anchors = []

		if (i > 1 && allblocks[i - 1] && allblocks[i - 1].type === 'anchor') {
			anchors.push(allblocks[i - 1].id)
		}

		// loop from startingblock to amount given in cms
		for (let c = 1; c < parseInt(harmonicaBlock.amount, 10) + 1; c++) {
			const currentBlock = allblocks[c + i]
			if (currentBlock && currentBlock.type === 'anchor') {
				anchors.push(currentBlock.id)
			}
			// check if harmonica in harmonicablock

			if (currentBlock && currentBlock.type === 'harmonica') {
				// loop over harmonica
				this.catchHarmonicaBlocks(allblocks, currentBlock, c + i, blockKey)
				i += parseInt(currentBlock.amount, 10)
			}

			this.skipindex++
			content.push(currentBlock)
		}
		if (window.location.hash && anchors.length) {
			harmonicaBlock.defaultOpen =
				anchors.indexOf(window.location.hash.slice(1)) >= 0
		}
		harmonicaBlock.content = content
		return renderSwitch(harmonicaBlock, blockKey)
	}
}

export const renderSwitch = (block, blockKey = '') => {
	if (!block || !block.type) return null
	switch (block.type) {
		case 'text':
			return <TextBlock {...block} />
		case 'intro':
			return <IntroBlock {...block} />
		case 'image':
			return <ImageBlock {...block} />
		case 'imagetext':
			return <ImageTextBlock {...block} />
		case 'embed':
			return <EmbedBlock {...block} />
		case 'collection':
			return <CollectionBlock {...block} />
		case 'references':
			return <ReferencesBlock {...block} />
		case 'button':
			return <ButtonBlock {...block} />
		case 'harmonica':
			return <HarmonicaBlock {...block} />
		case 'person':
			return <PersonBlock {...block} />
		case '2columns':
			return <ColumnBlock {...block} />
		case 'previewlink':
			return <PreviewLinkBlock {...block} />
		case 'previewlinkgroup':
			return <PreviewLinkGroupBlock {...block} />
		case 'quote':
			return <QuoteBlock {...block} />
		case 'chart':
			return <ChartBlock {...block} key={blockKey} />
		case 'divider':
			return <DividerBlock {...block} />
		case 'audio':
			return <AudioBlock {...block} />
		case 'cookiedeclaration':
			return <CookiesdeclarationBlock {...block} />
		case 'matomo':
			return <MatomoBlock {...block} />
		case 'anchor':
			return <AnchorBlock {...block} />
		default:
			return <p>Unknown block type: {block.type}</p>
	}
}

export const Block: FunctionComponent<DOMAttrs> = ({
	children,
	style: blockStyle,
	id,
	...props
}) => (
	<div
		class={styles.block.mergeProps(props)}
		style={blockStyle}
		{...(id ? {id} : {})}
	>
		{children}
	</div>
)
